/*
  PRP mobile buy flow
  1. Collapsible product description with Read more toggle (mobile only)
  2. Sticky Add to Cart bar that proxies the real product form submit button
  3. Mobile back link

  This file never submits its own form. It clicks the existing
  #ProductSubmitButton-{section} so product-form.js owns the request,
  validation, error handling and cart drawer rendering.
*/
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 48em)';
  var mq = window.matchMedia(MOBILE_QUERY);

  /* ---------------------------------------------------------------
     Read more
  --------------------------------------------------------------- */
  function initReadMore(wrapper) {
    if (wrapper.dataset.readMoreReady) return;
    wrapper.dataset.readMoreReady = 'true';

    var content = wrapper.querySelector('.product-detail__description');
    var toggle = wrapper.querySelector('[data-read-more-toggle]');
    if (!content || !toggle) return;

    var label = toggle.querySelector('span');
    var moreText = toggle.dataset.labelMore || 'Read more';
    var lessText = toggle.dataset.labelLess || 'Read less';

    function collapsedHeight() {
      var raw = getComputedStyle(wrapper).getPropertyValue('--description-collapsed-height');
      var px = parseFloat(raw);
      if (raw.indexOf('rem') > -1) px = px * parseFloat(getComputedStyle(document.documentElement).fontSize);
      return isNaN(px) ? 160 : px;
    }

    function evaluate() {
      if (!mq.matches) {
        wrapper.classList.remove('is-collapsible');
        return;
      }
      var wasCollapsible = wrapper.classList.contains('is-collapsible');
      wrapper.classList.remove('is-collapsible');
      var full = content.scrollHeight;
      if (full > collapsedHeight() + 48) {
        wrapper.classList.add('is-collapsible');
      } else if (wasCollapsible) {
        wrapper.classList.remove('is-expanded');
        toggle.setAttribute('aria-expanded', 'false');
      }
    }

    toggle.addEventListener('click', function () {
      var expanded = wrapper.classList.toggle('is-expanded');
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (label) label.textContent = expanded ? lessText : moreText;
      if (!expanded) {
        var top = wrapper.getBoundingClientRect().top;
        if (top < 0) wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    evaluate();
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(evaluate, 150);
    });
    mq.addEventListener('change', evaluate);
  }

  /* ---------------------------------------------------------------
     Sticky Add to Cart
  --------------------------------------------------------------- */
  function initStickyAtc(bar) {
    if (bar.dataset.stickyReady) return;
    bar.dataset.stickyReady = 'true';

    var sectionId = bar.dataset.section;
    var primary = document.getElementById('ProductSubmitButton-' + sectionId);
    var form = document.getElementById(bar.dataset.formId);
    var button = bar.querySelector('[data-sticky-atc-button]');
    var label = bar.querySelector('[data-sticky-atc-label]');
    var priceOut = bar.querySelector('[data-sticky-atc-price]');
    var priceSrc = document.getElementById('price-' + sectionId);

    if (!primary || !form || !button) return;

    function primaryLabel() {
      var spans = primary.querySelectorAll(':scope > span');
      for (var i = 0; i < spans.length; i++) {
        if (!spans[i].classList.contains('hidden')) return spans[i].textContent.trim();
      }
      return '';
    }

    function sync() {
      var disabled =
        primary.hasAttribute('disabled') || primary.getAttribute('aria-disabled') === 'true';
      if (disabled) {
        button.setAttribute('disabled', 'disabled');
      } else {
        button.removeAttribute('disabled');
      }
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.classList.toggle('loading', primary.classList.contains('loading'));

      var text = primaryLabel();
      if (label && text) label.textContent = text;

      if (priceSrc && priceOut) {
        var onSale = priceSrc.querySelector('.price--on-sale');
        var el = onSale
          ? priceSrc.querySelector('.price__sale .price-item--sale')
          : priceSrc.querySelector('.price__regular .price-item--regular');
        if (el) priceOut.textContent = el.textContent.trim();
      }
    }

    var observerConfig = { attributes: true, childList: true, subtree: true, characterData: true };
    new MutationObserver(sync).observe(primary, observerConfig);
    if (priceSrc) new MutationObserver(sync).observe(priceSrc, { childList: true, subtree: true, characterData: true });
    sync();

    button.addEventListener('click', function () {
      if (button.hasAttribute('disabled')) return;
      primary.click();
      // If product-form.js blocked the submit (e.g. ambassador not chosen),
      // bring the form and its error message into view.
      window.requestAnimationFrame(function () {
        var productForm = form.closest('product-form');
        var error = productForm ? productForm.querySelector('.product-form__error-message-wrapper') : null;
        if (error && !error.hidden) {
          productForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });

    function setVisible(on) {
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('sticky-atc-active', on);
    }

    function passedPrimary() {
      return primary.getBoundingClientRect().bottom < 0;
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          var passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          setVisible(mq.matches && passed);
        },
        { threshold: 0 }
      );
      io.observe(primary);
    } else {
      window.addEventListener('scroll', function () {
        setVisible(mq.matches && passedPrimary());
      }, { passive: true });
    }

    mq.addEventListener('change', function () {
      setVisible(mq.matches && passedPrimary());
    });
  }

  /* ---------------------------------------------------------------
     Back link (mobile). Uses history when the visitor came from
     this site, otherwise falls through to the href (collection URL).
  --------------------------------------------------------------- */
  function initBackLink(link) {
    if (link.dataset.backReady) return;
    link.dataset.backReady = 'true';
    link.addEventListener('click', function (event) {
      var sameOrigin = document.referrer && document.referrer.indexOf(window.location.origin) === 0;
      if (sameOrigin && window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
    });
  }

  /* ---------------------------------------------------------------
     Boot
  --------------------------------------------------------------- */
  function init(root) {
    root.querySelectorAll('[data-read-more]').forEach(initReadMore);
    root.querySelectorAll('[data-sticky-atc]').forEach(initStickyAtc);
    root.querySelectorAll('[data-product-back]').forEach(initBackLink);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
